import React, { useState, useEffect } from 'react'
import { Formik, ErrorMessage } from 'formik'
import * as Yup from 'yup'
import moment from 'moment'
import {
  Row,
  Col,
  Form,
  FormGroup,
  Button,
  Input
} from 'reactstrap'
import { useTranslation } from 'react-i18next'
import { get } from 'lodash'
import Select from 'ui/forms/select'
import DatePicker from 'ui/forms/datetime_picker'
import QtyInput from 'ui/forms/qty_input'
import time from 'i18n/time'

const MIN_SENIOR_AGE = 55
const MAX_SENIOR_AGE = 80
const MIN_CHILD_AGE = 0
const MAX_CHILD_AGE = 17

const ageOptions = (min, max) => (
  Array.from(
    { length: max - min + 1 },
    (_, index) => min + index
  )
)

const SENIOR_AGE_OPTIONS = ageOptions(
  MIN_SENIOR_AGE,
  MAX_SENIOR_AGE
)

const CHILD_AGE_OPTIONS = ageOptions(
  MIN_CHILD_AGE,
  MAX_CHILD_AGE
)

const QuoteForm = (props) => {
  const { i18n } = useTranslation()
  const { config, machine } = props
  const [isReversed, setIsReversed] = useState(false)

  const release = get(
    config,
    'product.external_api_config.release'
  )

  const purchaseInfo = config.product.purchase_info
  const places = purchaseInfo.places
  const accommodation = purchaseInfo.accommodation

  const pickupOptions = isReversed
    ? [accommodation]
    : places

  const dropoffOptions = isReversed
    ? places
    : [accommodation]

  const incrementQty = (currentQty, maxQty) => (
    currentQty >= maxQty
      ? maxQty
      : currentQty + 1
  )

  const decrementQty = (currentQty, minQty) => (
    currentQty <= minQty
      ? minQty
      : currentQty - 1
  )

  const requiredMsg = i18n.t('validations:required')

  const invalidDateMsg = i18n.t('validations:invalid_date')

  const invalidAgeMsg = i18n.t('validations:invalid_age')

  const parseDate = (value) => {
    if (value instanceof Date) {
      return moment(value)
    }

    if (
      typeof value === 'string' &&
      value.trim()
    ) {
      return moment.parseZone(
        value,
        moment.ISO_8601,
        true
      )
    }

    return moment.invalid()
  }

  const dateSchema = () => Yup.mixed()
    .transform(
      (value, originalValue) => (
        originalValue === ''
          ? null
          : value
      )
    )
    .nullable()
    .test(
      'valid-date',
      invalidDateMsg,
      (value) => (
        value == null ||
        parseDate(value).isValid()
      )
    )

  const passengerCount = (minimum) => Yup.number()
    .typeError(
      i18n.t('validations:invalid_number')
    )
    .integer(
      i18n.t('validations:integer')
    )
    .min(
      minimum,
      i18n.t(
        'validations:min_passengers',
        {
          min: minimum
        }
      )
    )
    .max(
      20,
      i18n.t(
        'validations:max_passengers',
        {
          max: 20
        }
      )
    )
    .required(requiredMsg)

  const passengerAge = (
    minAge,
    maxAge
  ) => Yup.number()
    .transform(
      (value, originalValue) => (
        originalValue === '' ||
        originalValue == null
          ? null
          : value
      )
    )
    .nullable()
    .typeError(invalidAgeMsg)
    .integer(invalidAgeMsg)
    .min(minAge, invalidAgeMsg)
    .max(maxAge, invalidAgeMsg)
    .required(requiredMsg)

  const passengerAges = (
    countField,
    minAge,
    maxAge
  ) => Yup.array()
    .of(passengerAge(minAge, maxAge))
    .test(
      `${countField}-age-count`,
      requiredMsg,
      function (ages) {
        const count = Number(
          this.parent[countField] || 0
        )

        return (ages || []).length === count
      }
    )

  const validationSchema = Yup.object({
    pickup: Yup.mixed()
      .transform(
        (value, originalValue) => (
          originalValue === ''
            ? null
            : value
        )
      )
      .required(requiredMsg),

    dropoff: Yup.mixed()
      .transform(
        (value, originalValue) => (
          originalValue === ''
            ? null
            : value
        )
      )
      .required(requiredMsg),

    trip_type: Yup.string()
      .oneOf(
        ['one_way', 'round_trip'],
        requiredMsg
      )
      .required(requiredMsg),

    adults: passengerCount(1),
    seniors: passengerCount(0),
    children: passengerCount(0),

    senior_ages: passengerAges(
      'seniors',
      MIN_SENIOR_AGE,
      MAX_SENIOR_AGE
    ),

    children_ages: passengerAges(
      'children',
      MIN_CHILD_AGE,
      MAX_CHILD_AGE
    ),

    pickup_date: dateSchema()
      .required(requiredMsg),

    return_date: Yup.mixed()
      .when('trip_type', {
        is: 'round_trip',

        then: () => dateSchema()
          .nullable()
          .notRequired()
          .test(
            'return-after-pickup',
            i18n.t('validations:return_date_before_pickup'),
            function (value) {
              if (!value) return true

              const pickupDate = parseDate(
                this.parent.pickup_date
              )

              const returnDate = parseDate(value)

              if (
                !pickupDate.isValid() ||
                !returnDate.isValid()
              ) {
                return true
              }

              return (
                returnDate.format('YYYY-MM-DD') >=
                pickupDate.format('YYYY-MM-DD')
              )
            }
          ),

        otherwise: (schema) => (
          schema
            .nullable()
            .notRequired()
        )
      })
  })

  const defaultPickup = time()
    .add('2', 'days')
    .toDate()

  const defaultReturn = time()
    .add('7', 'days')
    .toDate()

  return (
    <Formik
      initialValues={{
        pickup: places[0]?.value ?? '',
        dropoff: accommodation?.value ?? '',
        adults: 1,
        seniors: 0,
        children: 0,
        senior_ages: [],
        children_ages: [],
        pickup_date: defaultPickup,
        return_date: defaultReturn,
        trip_type: 'round_trip'
      }}
      validationSchema={validationSchema}
      onSubmit={() => {}}
      onReset={() => setIsReversed(false)}
      validateOnBlur
      validateOnChange
    >
      {(form) => {
        const {
          values,
          errors,
          touched,
          setFieldValue,
          setFieldTouched,
          setValues,
          handleSubmit,
          handleReset
        } = form

        const switchDirection = () => {
          setValues((current) => ({
            ...current,
            pickup: current.dropoff,
            dropoff: current.pickup
          }))

          setIsReversed(
            (current) => !current
          )
        }

        const setTripType = (tripType) => {
          setFieldValue(
            'trip_type',
            tripType
          )

          if (tripType === 'one_way') {
            setFieldValue(
              'return_date',
              ''
            )

            setFieldTouched(
              'return_date',
              false
            )

            return
          }

          if (
            tripType === 'round_trip' &&
            !values.return_date
          ) {
            setFieldValue(
              'return_date',
              defaultReturn
            )
          }
        }

        const incrementPassengerWithAge = (
          countField,
          agesField
        ) => {
          const currentQty =
            Number(values[countField] || 0)

          const nextQty = incrementQty(
            currentQty,
            20
          )

          if (nextQty === currentQty) {
            return
          }

          setFieldValue(
            countField,
            nextQty
          )

          setFieldValue(
            agesField,
            [
              ...(values[agesField] || []),
              ''
            ]
          )
        }

        const decrementPassengerWithAge = (
          countField,
          agesField
        ) => {
          const currentQty =
            Number(values[countField] || 0)

          const nextQty = decrementQty(
            currentQty,
            0
          )

          if (nextQty === currentQty) {
            return
          }

          setFieldValue(
            countField,
            nextQty
          )

          setFieldValue(
            agesField,
            (values[agesField] || [])
              .slice(0, nextQty)
          )
        }

        const removePassengerAge = (
          countField,
          agesField,
          index
        ) => {
          const ages = [
            ...(values[agesField] || [])
          ]

          ages.splice(index, 1)

          setFieldValue(
            agesField,
            ages
          )

          setFieldValue(
            countField,
            Math.max(
              Number(values[countField] || 0) - 1,
              0
            )
          )
        }

        const setPassengerAge = (
          agesField,
          index,
          age
        ) => {
          const ages = [
            ...(values[agesField] || [])
          ]

          ages[index] = (
            age === ''
              ? ''
              : Number(age)
          )

          setFieldValue(
            agesField,
            ages
          )
        }

        const formatAge = (age) => (
          i18n.t(
            'axis_quote_form.age_years',
            {
              count: age
            }
          )
        )

        const renderAgeRows = ({
          count,
          countField,
          agesField,
          ages,
          options,
          title,
          passengerLabel
        }) => {
          if (!count) return null

          return (
            <div className="mt-3 pt-3">
              <div className="small text-body-secondary mb-3">
                {title}
              </div>

              <div className="d-flex flex-column">
                {ages.map((age, index) => (
                  <div
                    key={`${agesField}-${index}`}
                    className="d-flex align-items-center justify-content-between gap-3 py-2 border-bottom"
                  >
                    <div className="d-flex align-items-center gap-2">
                      <Button
                        type="button"
                        color="link"
                        className="p-0 border-0 text-danger text-decoration-none"
                        aria-label={i18n.t('axis_quote_form.remove_passenger')}
                        onClick={() => (
                          removePassengerAge(
                            countField,
                            agesField,
                            index
                          )
                        )}
                      >
                        <i
                          className="fa fa-times"
                          aria-hidden="true"
                        />
                      </Button>

                      <span className="text-body">
                        {passengerLabel}{' '}
                        {index + 1}
                      </span>
                    </div>

                    <Input
                      type="select"
                      value={age}
                      className="w-auto"
                      style={{ minWidth: '110px' }}
                      aria-label={i18n.t(
                        'axis_quote_form.passenger_age',
                        {
                          number: index + 1
                        }
                      )}
                      onChange={(event) => (
                        setPassengerAge(
                          agesField,
                          index,
                          event.target.value
                        )
                      )}
                    >
                      <option value="">
                        {i18n.t('axis_quote_form.select_age')}
                      </option>

                      {options.map((optionAge) => (
                        <option
                          key={optionAge}
                          value={optionAge}
                        >
                          {formatAge(optionAge)}
                        </option>
                      ))}
                    </Input>
                  </div>
                ))}
              </div>

              <ErrorMessage
                name={agesField}
                component="div"
                className="text-danger small mt-2"
              />
            </div>
          )
        }

        useEffect(() => {
          props.setForm(form)
        }, [values, errors, touched])

        return (
          <Form
            className="w-50"
            onSubmit={handleSubmit}
            onReset={handleReset}
          >
            <div className="accordion">
              <div className="accordion-item rounded overflow-hidden">
                <div
                  className="accordion-header d-flex flex-nowrap border-bottom"
                  role="group"
                  aria-label={i18n.t('axis_quote_form.trip_type')}
                >
                  {[
                    {
                      value: 'one_way',
                      label: i18n.t('axis_quote_form.one_way')
                    },
                    {
                      value: 'round_trip',
                      label: i18n.t('axis_quote_form.round_trip')
                    }
                  ].map((trip) => {
                    const isSelected =
                      values.trip_type === trip.value

                    return (
                      <button
                        key={trip.value}
                        id={`quote-${trip.value}-heading`}
                        type="button"
                        className={`btn rounded-0 py-2 fw-bold ${isSelected ? 'btn-primary' : 'btn-light'}`}
                        style={{
                          flex: '1 1 0',
                          minWidth: 0
                        }}
                        aria-pressed={isSelected}
                        aria-controls="quote-trip-panel"
                        onClick={() => (
                          setTripType(trip.value)
                        )}
                      >
                        {trip.label}
                      </button>
                    )
                  })}
                </div>

                <div
                  id="quote-trip-panel"
                  className="accordion-body"
                  role="region"
                  aria-labelledby={`quote-${values.trip_type}-heading`}
                >
                  <Row className="justify-content-md-center">
                    <Col xs="12" md="12">
                      <div className="d-flex">
                        <div
                          className="w-100"
                          style={{ minWidth: 0 }}
                        >
                          {pickupOptions.length === 1 ? (
                            <FormGroup>
                              <div
                                className="control-label mb-2 text-center"
                                id="pickup-label"
                              >
                                {i18n.t('axis_quote_form.pickup')}
                              </div>

                              <div
                                className="form-control-plaintext bg-light rounded text-center border fw-bold"
                                aria-labelledby="pickup-label"
                                style={{
                                  cursor: 'not-allowed'
                                }}
                              >
                                {pickupOptions[0].label}
                              </div>
                            </FormGroup>
                          ) : (
                            <Select
                              name="pickup"
                              inputId="pickup"
                              label={i18n.t('axis_quote_form.pickup')}
                              values={pickupOptions}
                              value={
                                pickupOptions.find(
                                  (item) => (
                                    item.value === values.pickup
                                  )
                                ) ?? null
                              }
                              placeholder={i18n.t('axis_quote_form.pickup_placeholder')}
                              required
                            />
                          )}
                        </div>

                        <div className="mx-3 d-flex align-self-center mt-3">
                          <Button
                            type="button"
                            color="primary"
                            onClick={switchDirection}
                            aria-label={i18n.t('axis_quote_form.switch_direction')}
                            title={i18n.t('axis_quote_form.switch_direction')}
                          >
                            <i
                              className="fas fa-exchange-alt"
                              aria-hidden="true"
                            />
                          </Button>
                        </div>

                        <div
                          className="w-100"
                          style={{ minWidth: 0 }}
                        >
                          {dropoffOptions.length === 1 ? (
                            <FormGroup>
                              <div
                                className="control-label mb-2 text-center"
                                id="dropoff-label"
                              >
                                {i18n.t('axis_quote_form.dropoff')}
                              </div>

                              <div
                                className="form-control-plaintext bg-light rounded text-center border fw-bold"
                                aria-labelledby="dropoff-label"
                                style={{
                                  cursor: 'not-allowed'
                                }}
                              >
                                {dropoffOptions[0].label}
                              </div>
                            </FormGroup>
                          ) : (
                            <Select
                              name="dropoff"
                              inputId="dropoff"
                              label={i18n.t('axis_quote_form.dropoff')}
                              values={dropoffOptions}
                              value={
                                dropoffOptions.find(
                                  (item) => (
                                    item.value === values.dropoff
                                  )
                                ) ?? null
                              }
                              placeholder={i18n.t('axis_quote_form.dropoff_placeholder')}
                              required
                            />
                          )}
                        </div>
                      </div>
                    </Col>

                    <Col xs="12" md="12">
                      <FormGroup>
                        <DatePicker
                          machine={machine}
                          name="pickup_date"
                          onBlur={() => (
                            setFieldTouched(
                              'pickup_date',
                              true
                            )
                          )}
                          release={release}
                          label={i18n.t('axis_quote_form.pickup_date')}
                          timezone={
                            config.product.local_timezone
                          }
                          showTimeSelect={false}
                          dateFormat={'P'}
                          initialDate={defaultPickup}
                        />

                        <ErrorMessage
                          name="pickup_date"
                          component="div"
                          className="text-danger small"
                        />
                      </FormGroup>
                    </Col>

                    {values.trip_type === 'round_trip' && (
                      <Col xs="12" md="12">
                        <FormGroup>

                          <DatePicker
                            machine={machine}
                            name="return_date"
                            onBlur={() => (
                              setFieldTouched(
                                'return_date',
                                true
                              )
                            )}
                            release={release}
                            label={i18n.t('axis_quote_form.return_date')}
                            timezone={
                              config.product.local_timezone
                            }
                            showTimeSelect={false}
                            dateFormat={'P'}
                            initialDate={defaultReturn}
                          />

                          <ErrorMessage
                            name="return_date"
                            component="div"
                            className="text-danger small"
                          />
                        </FormGroup>
                      </Col>
                    )}

                    <Col xs="12" md="12">
                      <FormGroup>
                        <hr />

                        <Row>
                          <Col xs="12" md="4">
                            <QtyInput
                              label={i18n.t(
                                'format:age_group.adults'
                              )}
                              name="adults"
                              value={values.adults}
                              onIncrement={() => (
                                setFieldValue(
                                  'adults',
                                  incrementQty(
                                    values.adults,
                                    20
                                  )
                                )
                              )}
                              onDecrement={() => (
                                setFieldValue(
                                  'adults',
                                  decrementQty(
                                    values.adults,
                                    1
                                  )
                                )
                              )}
                            />
                          </Col>

                          <Col xs="12" md="4">
                            <QtyInput
                              label={i18n.t(
                                'format:age_group.seniors'
                              )}
                              name="seniors"
                              value={values.seniors}
                              onIncrement={() => (
                                incrementPassengerWithAge(
                                  'seniors',
                                  'senior_ages'
                                )
                              )}
                              onDecrement={() => (
                                decrementPassengerWithAge(
                                  'seniors',
                                  'senior_ages'
                                )
                              )}
                            />
                          </Col>

                          <Col xs="12" md="4">
                            <QtyInput
                              label={i18n.t('format:age_group.children')}
                              name="children"
                              value={values.children}
                              onIncrement={() => (
                                incrementPassengerWithAge(
                                  'children',
                                  'children_ages'
                                )
                              )}
                              onDecrement={() => (
                                decrementPassengerWithAge(
                                  'children',
                                  'children_ages'
                                )
                              )}
                            />
                          </Col>
                        </Row>

                        {renderAgeRows({
                          count: values.seniors,
                          countField: 'seniors',
                          agesField: 'senior_ages',
                          ages: values.senior_ages,
                          options: SENIOR_AGE_OPTIONS,
                          title: i18n.t('axis_quote_form.senior_ages_title'),
                          passengerLabel: i18n.t('axis_quote_form.senior_age')
                        })}

                        {renderAgeRows({
                          count: values.children,
                          countField: 'children',
                          agesField: 'children_ages',
                          ages: values.children_ages,
                          options: CHILD_AGE_OPTIONS,
                          title: i18n.t('axis_quote_form.children_ages_title'),
                          passengerLabel: i18n.t('axis_quote_form.child_age')
                        })}
                      </FormGroup>
                    </Col>
                  </Row>
                </div>
              </div>
            </div>
          </Form>
        )
      }}
    </Formik>
  )
}

export default QuoteForm
